import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Check, MessageSquare, X } from 'lucide-react'
import {
  currentBuild,
  sendFeedback,
  sendSupportRequest,
  FEEDBACK_KINDS,
  type FeedbackKind,
} from '../api/feedback'
import { useSession } from '../useSession'

/**
 * Telling us what you found, from wherever you found it (D-607).
 *
 * <b>In the header, because a pilot tester will not go looking for it.</b> The
 * whole value of asking inside the app is that the thought and the form are in
 * the same place — a feedback page somebody has to navigate to collects
 * feedback from people who were already going to email you.
 *
 * <b>Three questions and one of them is optional.</b> A kind, a box, and send.
 * Who she is comes from her login and the screen comes from the router, so the
 * two fields that make a report actionable cost her nothing.
 *
 * <b>It says what it captured.</b> Sending a device's page address somewhere
 * without saying so would be a small deception on a form built to earn trust,
 * and naming it also tells her when to add "on the register screen" herself.
 */
export default function FeedbackButton() {
  const { session } = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  /*
   * <b>Whether this host can hold a conversation at all</b> (D-838). Everything
   * below turns on it, because a support thread is a route inside the messaging
   * module and 404s where the module is off. Decided from what the session says
   * the host has, never assumed: a control that offers a door which refuses the
   * person holding it is the shape D-333 keeps naming.
   */
  const messaging = session?.modules?.messaging !== false

  /*
   * <b>Everybody is offered everything, and every one of them opens a thread.</b>
   * `asked`, 2026-09-27: *"The feedback thing should open the thread with Becky
   * and I for all options not just the need help. Tonya doesn't have time for that
   * stuff."*
   *
   * ⚠ <b>This replaces a split that existed for one commit</b>, where three kinds
   * filed a one-way row on an owner-only list and only the fourth could be
   * answered — so a parent was shown the fourth alone, because picking a silent
   * one and waiting for a reply is the failure that mattered. **Every kind
   * answering back removes the reason for the split**, and with it the special
   * case: one list, one behaviour, for staff and families alike.
   *
   * <b>The kind stays</b>, because a bug and a request are not read the same way
   * and the filed row still carries it for triage. What it no longer decides is
   * whether anybody can reply.
   *
   * <b>With messaging off, the last option goes</b>: *I need help with something*
   * promises a reply this host cannot deliver, while the other three are honest
   * about being a note. That is the only case where the list is not all four.
   */
  const kinds = messaging ? FEEDBACK_KINDS : FEEDBACK_KINDS.filter((k) => k.value !== 'Support')

  const [open, setOpen] = useState(false)
  /*
   * ⚠ <b>Derived, never seeded from the session</b> (D-838). `useSession` is a
   * query, so on the very first render — which is the one `useState`'s initialiser
   * runs on — it has not answered and every flag reads false. Seeding the default
   * from it therefore defaulted <b>staff and the owner</b> to *Support*, and an
   * audit watched a staff bug report open a support thread instead of filing a
   * candid row. The list widened when the session landed; the selection did not.
   *
   * So state holds only what she actually picked, and the default is worked out at
   * render from whatever is known by then.
   */
  const [picked, setPicked] = useState<FeedbackKind | null>(null)

  const kind: FeedbackKind = picked && kinds.some((k) => k.value === picked)
    ? picked
    : (kinds[0]?.value ?? 'Problem')

  const setKind = setPicked
  const [body, setBody] = useState('')
  const [sent, setSent] = useState(false)

  const send = useMutation({
    mutationFn: async () => {
      // Where she was when she pressed it, which is the screen she is
      // describing — captured before the dialog changed anything.
      const path = location.pathname + location.search
      const build = currentBuild()

      /*
       * <b>Every kind goes to the messaging module, which is what makes it
       * answerable</b> (D-838). That route is also what applies the gates writing
       * a conversation has to pass — whether the account may send at all, the
       * unconsented-dancer refusal, the rate limit — none of which
       * `/api/feedback` carries, deliberately, because filing a note needs none.
       *
       * <b>The one-way door is kept for a host with no messaging</b>, and only
       * for that: it is the difference between a button that files a note and a
       * button that cannot do anything. Not a second way to say the same thing —
       * the two are not the same thing, and which one applies is the host's
       * answer rather than a choice anybody makes.
       */
      if (messaging) return sendSupportRequest({ kind, body, path, build })

      await sendFeedback({ kind, body, path, build })
      return null
    },
    onSuccess: (thread) => {
      setBody('')

      /*
       * <b>Support lands her in the thread rather than on a confirmation</b>
       * (D-838). `asked`: *"takes them to their messaging page so they see they
       * now have a thread with support."* A "we got it" panel would be the exact
       * silence this option exists to end — the point is that she can see the
       * conversation, and reply to it.
       */
      if (thread) {
        setOpen(false)
        navigate(`/messages/${thread.conversationId}`)
        return
      }

      setSent(true)
    },
  })

  /*
   * <b>Not on a wall tablet either.</b> A PIN-unlocked device has a session,
   * so guarding on that alone drew the button on a screen whose POST the kiosk
   * boundary refuses (D-233) — a control that answers "not available on a
   * studio tablet" when pressed. The bell one element over already guards this
   * way, and a link that refuses the person holding it is worse than no link.
   */
  if (!session || session.isKiosk === true) return null

  const close = () => {
    setOpen(false)
    setSent(false)
    setPicked(null)
    send.reset()
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[2.25rem] items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
      >
        <MessageSquare size={14} strokeWidth={2} />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Send feedback"
        >
          <div className="w-full max-w-[34rem] rounded-2xl border border-line bg-surface p-5 shadow-xl">
            <div className="mb-3 flex items-start gap-3">
              <h2 className="min-w-0 flex-1 font-display text-xl">
                {sent ? 'Thank you' : 'Tell us what you found'}
              </h2>

              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
              >
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>

            {sent ? (
              <>
                <p className="flex items-start gap-2 text-[13.5px]">
                  <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-good" />
                  That is on the list. If it needs a conversation somebody will be in touch.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
                  >
                    Send another
                  </button>

                  <button
                    type="button"
                    onClick={close}
                    className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (body.trim() && !send.isPending) send.mutate()
                }}
              >
                <fieldset className="mb-3">
                  <legend className="mb-1.5 text-[12px] font-semibold text-ink-3">
                    What kind of thing is it?
                  </legend>

                  <div className="flex flex-col gap-1.5">
                    {kinds.map((option) => (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer items-start gap-2.5 rounded-xl border px-3 py-2.5 ${
                          kind === option.value
                            ? 'border-accent bg-surface-2'
                            : 'border-line hover:border-line-2'
                        }`}
                      >
                        <input
                          type="radio"
                          name="feedback-kind"
                          value={option.value}
                          checked={kind === option.value}
                          onChange={() => setKind(option.value)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="block text-[13.5px] font-semibold">{option.label}</span>
                          <span className="block text-[12.5px] text-ink-3">{option.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold text-ink-3">
                    What happened?
                  </span>

                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    autoFocus
                    placeholder={
                      kind === 'Problem'
                        ? 'What you were doing, and what it did instead.'
                        : 'As much or as little as you like.'
                    }
                    className="w-full rounded-xl border border-line bg-surface-2 px-3 py-2 text-[13.5px] outline-none focus:border-accent"
                  />
                </label>

                {/*
                  Said out loud, because sending it silently would be a small
                  deception on a form built to earn trust — and naming the
                  screen is also what tells her she need not describe it.
                */}
                <p className="mt-1.5 text-[11.5px] text-ink-3">
                  Sent with your name and the screen you are on
                  {location.pathname !== '/' && <> ({location.pathname})</>}, so nobody has to ask.
                </p>

                {send.isError && (
                  <p role="alert" className="mt-2 text-[12.5px] text-crit">
                    {(send.error as Error).message}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={!body.trim() || send.isPending}
                    className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-50"
                  >
                    {/*
                      One word for all four, because all four do one thing now
                      (D-838). It said *Send to support* for the help option
                      alone, which read as though the others went somewhere else.
                    */}
                    {send.isPending ? 'Sending...' : 'Send to support'}
                  </button>

                  <button
                    type="button"
                    onClick={close}
                    className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  )
}
